import { FC, useState, useEffect, useMemo } from 'react'
import { useField, FormikProps } from 'formik'
import clsx from 'clsx'
import { CheckCircle, CheckSquare, Circle, Square } from 'react-feather'
import { CloseCircleFilled } from '@ant-design/icons'
import unionBy from 'lodash.unionby'

import { ReactComponent as TooltipInfo } from 'assets/images/mini_tooltip.svg'

import ComponentPicker from '../ComponentPicker'

import { Select, Tooltip } from '@pepper/ui'

import styles from './_.module.css'

type Option = {
  value: string | number
  label: string
  onSelectDisableOptions?: string[] | number[]
}

type CustomFieldProps =
  | {
      showCustomField: true
      placeHolder?: string
      customFieldInfo?: React.ReactNode
      customFieldOptions?: Array<Option>
    }
  | {
      showCustomField?: false
      placeHolder?: never
      customFieldInfo?: never
      customFieldOptions?: never
    }

type CommonProps = {
  name: string
  singleSelect: boolean
  label?: string
  required?: boolean
  options: Array<Option>
  actions: FormikProps<any>
  body?: IProjectBody[]
  miniTooltip?: string
  disabled?: boolean
}

type Props = CommonProps & CustomFieldProps

const CheckboxRadio: FC<Props> = ({
  name,
  label,
  required,
  options,
  singleSelect,
  body,
  actions,
  showCustomField = false,
  placeHolder,
  customFieldInfo,
  miniTooltip,
  customFieldOptions = [],
  disabled,
}) => {
  const [field, meta, helpers] = useField(name)

  const valueLabelMapping = useMemo(
    () =>
      [...options, ...customFieldOptions].reduce<{
        [key: string | number]: string
      }>((mapping, option) => {
        mapping[option.value] = option.label
        return mapping
      }, {}),
    [options, customFieldOptions]
  )

  const [shouldShowCustomField, setToggleCustomField] = useState(false)
  const [checkboxOptions, setCheckboxOptions] = useState<Array<Option>>(options)

  useEffect(() => {
    if (showCustomField && field.value) {
      setCheckboxOptions(checkboxOptions => {
        if (singleSelect) {
          const isValueInOptions = checkboxOptions.find(
            option => option.value === field.value
          )
          return isValueInOptions
            ? checkboxOptions
            : [
                ...checkboxOptions,
                { value: field.value, label: valueLabelMapping[field.value] },
              ]
        }
        const valuesOptions = field.value.map((value: string | number) => ({
          value,
          label: valueLabelMapping[value],
        }))
        return unionBy(checkboxOptions, valuesOptions, 'value')
      })
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options, field.value, showCustomField])

  const checkTicked = (id: string | number) => {
    if (singleSelect) {
      return field.value === id
    }
    return field.value?.length > 0 && field.value.includes(id)
  }

  const setSelectorValue = (value: string | number, option: any) => {
    const checkExists = checkTicked(value)
    if (!checkExists) {
      // new checkbox option, so need to update options
      setCheckboxOptions(checkboxOptions => [
        ...checkboxOptions,
        {
          value,
          label: option.label,
        },
      ])
    }

    if (singleSelect) {
      // update the value of radio to match input value, existing value doesn't matter
      helpers.setValue(value)
    }

    // For multiselect, existing selected values need to be preserved
    const valueSet = field.value || []
    if (!checkExists) {
      // update the field value to include the new option
      helpers.setValue([...valueSet, value])
    }
  }

  const updateValue = (id: string | number) => {
    const checkExists = checkTicked(id)
    if (singleSelect) {
      checkExists ? helpers.setValue(null) : helpers.setValue(id)
    } else {
      const valueSet = field.value || []
      if (checkExists) {
        const indexValue = valueSet.indexOf(id)
        valueSet.splice(indexValue, 1)
        helpers.setValue(valueSet)
      } else {
        helpers.setValue([...valueSet, id])
      }
    }
  }

  return (
    <>
      <div className={styles.selectionWrap}>
        <div className={styles.header}>
          <span className={styles.labelText}>{label}</span>
          {required && <span className={styles.requiredText}>*</span>}
          {miniTooltip && (
            <Tooltip
              placement="top"
              title={() => (
                <div
                  dangerouslySetInnerHTML={{
                    __html: miniTooltip,
                  }}
                />
              )}
            >
              <TooltipInfo className={styles.tooltipPos} />
            </Tooltip>
          )}
        </div>
        <div className={styles.optionsWrap}>
          {checkboxOptions?.map((optionItem, index) => {
            const isActive = checkTicked(optionItem.value)
            const isDisabled = Array.isArray(optionItem.onSelectDisableOptions)
              ? optionItem.onSelectDisableOptions.some(_disable =>
                  field.value.includes(_disable)
                )
              : false

            return (
              <div
                role="menuitem"
                tabIndex={index}
                onKeyPress={() => {
                  if (disabled || isDisabled) return

                  setToggleCustomField(false)
                  updateValue(optionItem.value)
                }}
                onClick={() => {
                  if (disabled || isDisabled) return

                  setToggleCustomField(false)
                  updateValue(optionItem.value)
                }}
                key={index}
                className={clsx(styles.optionNew, {
                  [styles.optionActiveNew]: isActive,
                  [styles.optionDisabled]: disabled || isDisabled,
                })}
              >
                {/* changes the pill styling: no inputbox*/}
                {/* {getSelectIcon(isActive)} */}
                <span className={styles.optionText}>{optionItem.label}</span>
              </div>
            )
          })}
          {showCustomField && (
            <div
              role="menuitem"
              tabIndex={checkboxOptions.length}
              onKeyPress={() =>
                !disabled && setToggleCustomField(showCustom => !showCustom)
              }
              onClick={() =>
                !disabled && setToggleCustomField(showCustom => !showCustom)
              }
              className={clsx(styles.option, {
                [styles.optionActive]: shouldShowCustomField,
                [styles.optionDisabled]: disabled,
              })}
            >
              <span className={styles.optionText}>Custom</span>
            </div>
          )}
        </div>
        {showCustomField && shouldShowCustomField && (
          <>
            <Select
              mode="multiple"
              onChange={(values, options: any) =>
                setSelectorValue(
                  values[values.length - 1],
                  options[options.length - 1]
                )
              }
              disabled={disabled}
              optionFilterProp="label"
              placeholder={placeHolder}
              options={customFieldOptions.map(option => ({
                label: option.label,
                value: option.value,
              }))}
              className={clsx(styles.inputAreaV2, {
                [styles.borderRed]: meta.touched && meta.error,
              })}
            />
            {customFieldInfo && (
              <p className={styles.infoText}>{customFieldInfo}</p>
            )}
          </>
        )}
        {meta.touched && meta.error ? (
          <div className={styles.error}>
            <CloseCircleFilled className={styles.errorIcon} /> {meta.error}
          </div>
        ) : null}
      </div>

      {body?.map((bodyItem, index) => {
        const isPresent = checkTicked(bodyItem.conditionalVisibility.value)
        return isPresent ? (
          <div className={styles.subBody}>
            <ComponentPicker
              key={index}
              actions={actions}
              data={{ ...bodyItem, disabled }}
            />
          </div>
        ) : null
      })}
    </>
  )
}

export default CheckboxRadio
